// Part of the agent UI for spark#246 / @adr SPARK-0034-ai-agents-are-project-context-not-team-members@1: an AI agent is PROJECT CONTEXT, never a team
// member. Nothing here reaches the solver, and no agent carries a Powers profile — Powers is a
// psychometric instrument built for humans, and putting agents on that scale would argue they are
// interchangeable with candidates, which is the model @adr SPARK-0034-ai-agents-are-project-context-not-team-members@1 rejects.

import { FormattedMessage } from "react-intl";
import { useUiStore } from "@/stores/ui-store";
import { AgentMonogram } from "./AgentMonogram";
import { agentText } from "./agent-catalogue";
import type { AgentCatalogueEntry } from "./agent-catalogue";
import type { ReactNode } from "react";

function Column({ label, count, children }: { label: ReactNode; count?: number; children: ReactNode }) {
  return (
    <div>
      <div className="mb-3 flex items-baseline gap-2 text-[10px] tracking-[0.25em] text-ink/55">
        <span className="h-px w-4 bg-accent" aria-hidden />
        {label}
        {count != null ? <span className="text-ink/40 tabular-nums">{count}</span> : null}
      </div>
      {children}
    </div>
  );
}

/**
 * The full agent sheet: role, what it covers, tools, MCP surfaces, guardrails. Never Powers.
 *
 * Carries its own top padding: it is always rendered inside a `Modal` WITHOUT a `title` (the name
 * belongs in the sheet's own header, not in the modal's label slot), and that path has no `pt`.
 */
export function AgentDetail({ entry }: { entry: AgentCatalogueEntry }) {
  const locale = useUiStore((s) => s.locale);
  return (
    <div className="pt-6">
      <div className="flex items-center gap-4 border-b border-ink/10 pb-5">
        <AgentMonogram name={entry.name} />
        <div className="min-w-0">
          <a
            href={entry.url}
            target="_blank"
            rel="noreferrer noopener"
            className="font-serif text-xl leading-none underline decoration-ink/30 underline-offset-2 transition hover:text-accent focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
          >
            {entry.name}
          </a>
          <div className="mt-2 text-[10px] tracking-[0.08em] text-ink/45">
            {/* Product tokens, deliberately untranslated. */}
            {entry.vendor} / {entry.model} / v{entry.version} {/* i18n-allow: product tokens */}
          </div>
        </div>
      </div>

      <div className="mt-6 border-l-2 border-accent pl-4 text-sm leading-relaxed text-ink/80">
        {agentText(entry.role, locale)}
      </div>

      <div className="mt-7">
        <Column label={<FormattedMessage id="match.agent.covers" defaultMessage="Deckt ab" />}>
          <div className="flex flex-wrap gap-2">
            {entry.covers.map((c) => (
              <span key={c.en} className="box-flat bg-page px-2.5 py-1 text-[11px] text-ink/70">
                {agentText(c, locale)}
              </span>
            ))}
          </div>
        </Column>
      </div>

      {/* Two columns, not three: the tool list is long while MCP is a handful of chips, so a
          three-way split left most of the sheet empty. Stacking MCP over the guardrails fills the
          right column against the tools. */}
      <div className="mt-7 grid grid-cols-1 gap-x-12 gap-y-8 border-t border-ink/10 pt-6 @2xl:grid-cols-2">
        <Column label={<FormattedMessage id="match.agent.tools" defaultMessage="Tools" />} count={entry.tools.length}>
          <ul className="space-y-3">
            {entry.tools.map((t) => (
              <li key={t.name}>
                <div className="font-mono text-[12px] text-ink">{t.name}</div>
                <div className="text-[11px] leading-snug text-ink/55">{agentText(t.description, locale)}</div>
              </li>
            ))}
          </ul>
        </Column>

        <div className="space-y-8">
          <Column label={<FormattedMessage id="match.agent.mcp" defaultMessage="MCP" />} count={entry.mcp.length}>
            <div className="flex flex-wrap gap-2">
              {entry.mcp.map((m) => (
                <span key={m} className="box-flat px-2 py-1 font-mono text-[11px] text-ink/70">
                  {m}
                </span>
              ))}
            </div>
          </Column>

          <Column
            label={<FormattedMessage id="match.agent.guardrails" defaultMessage="Leitplanken" />}
            count={entry.guardrails.length}
          >
            <ol className="space-y-2">
              {entry.guardrails.map((g, i) => (
                <li key={g.en} className="flex gap-3 text-[11px] leading-snug text-ink/70">
                  <span className="font-mono text-accent tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                  <span>{agentText(g, locale)}</span>
                </li>
              ))}
            </ol>
          </Column>
        </div>
      </div>
    </div>
  );
}
